import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { keyCode } from '@bingads-webui/keycode';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { TextField } from '@bingads-webui-react/fabric-control';

import { getInfoPaneItemRenameStyle } from './styles/info-pane-item-rename-style';

const spinnerStyle = ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

export const InfoPaneItemRename = ({
  i18n,
  isReadonly,
  name,
  renameAsync,
  renderBlocker,
  promptInputLabel,
  inputPlaceholderText,
}) => {
  const classes = useDefaultStyles(getInfoPaneItemRenameStyle);

  const [displayInputBox, setDisplayInputBox] = useState(false);
  const [inputName, setInputName] = useState('');
  const [showUiBlocker, setShowUiBlocker] = useState(false);

  useEffect(() => {
    setDisplayInputBox(false);
    setShowUiBlocker(false);
    setInputName(name);
  }, [name]);

  const handleRename = useCallback(() => {
    setShowUiBlocker(true);
    renameAsync(inputName).finally(() => {
      setShowUiBlocker(false);
      setDisplayInputBox(false);
    });
  }, [inputName, renameAsync]);

  const handleDisplayInputBox = useCallback(() => {
    setInputName(name || '');
    setDisplayInputBox(true);
  }, [name]);

  const handleCancel = useCallback(() => {
    setInputName('');
    setDisplayInputBox(false);
  }, []);

  const onEnterPress = useCallback((event) => {
    if (event && event.keyCode === keyCode.Enter && inputName !== name) {
      handleRename();
    }
  }, [handleRename, inputName, name]);

  const onBlur = useCallback((event) => {
    if (!event.relatedTarget || (
      !event.relatedTarget.classList.contains('complete-rename') && !event.relatedTarget.classList.contains(classes.editWrapper)
    )) {
      setDisplayInputBox(false);
    }
  }, [classes.editWrapper]);

  return (
    <div className={classes.nameContentBox}>
      {
        name || displayInputBox ?
          <div className={classnames(classes.editNameBox, classes.nameBox)}>
            {
              displayInputBox ?
                <div className={classes.editWrapper} tabIndex={-1}>
                  <TextField
                    className={classnames(classes.textField, 'input-box')}
                    defaultValue={name || ''}
                    placeholder={name ? '' : inputPlaceholderText}
                    ariaLabel={i18n.getString(_TL_('rename'))}
                    onChange={(e, input) => { setInputName(input); }}
                    maxLength={512}
                    autoFocus
                    onKeyDown={onEnterPress}
                    onBlur={onBlur}
                  />
                  <button
                    className={classnames(
                      classes.icon, classes.completeIcon,
                      inputName === name && classes.disabledIcon,
                      'complete-rename'
                    )}
                    onClick={handleRename}
                    disabled={inputName === name}
                    aria-label={i18n.getString(_TL_('click to complete'))}
                  />
                  <button
                    className={classnames(classes.icon, classes.cancelIcon, 'cancel-rename')}
                    onClick={handleCancel}
                    aira-label={i18n.getString(_TL_('click to cancel'))}
                  />
                </div> :
                <div className={classes.editWrapper}>
                  <div className={classnames(classes.editContent, 'name-wrapper')}>{name}</div>
                  <button
                    className={classnames(classes.editIcon, classes.icon, 'rename-button', isReadonly && classes.disabledIcon)}
                    onClick={handleDisplayInputBox}
                    aria-label={i18n.getString(_TL_('click to rename'))}
                    disabled={isReadonly}
                  />
                </div>
            }
          </div> :
          <button
            className={classnames(classes.addNameBox, classes.nameBox)}
            onClick={handleDisplayInputBox}
            aria-label={i18n.getString(_TL_('click to add name'))}
            disabled={isReadonly}
          >
            <div
              className={classnames(classes.addIcon, classes.icon, isReadonly && classes.disabledIcon)}
            />
            <div className={classnames(classes.addText, isReadonly && classes.disabledIcon)}>
              {promptInputLabel}
            </div>
          </button>
        }
      {
        showUiBlocker && renderBlocker({ spinnerStyle })
      }
    </div>
  );
};

InfoPaneItemRename.propTypes = {
  name: PropTypes.string.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isReadonly: PropTypes.bool.isRequired,
  renameAsync: PropTypes.func.isRequired,
  renderBlocker: PropTypes.func.isRequired,
  promptInputLabel: PropTypes.string.isRequired,
  inputPlaceholderText: PropTypes.string.isRequired,
};
